import { PropsWithChildren, ReactNode, useState } from 'react';
import useAsyncFn from 'react-use/lib/useAsyncFn';

import { Banner, Flex } from '@spotify-internal/encore-web';

import { DialogComposite } from './DialogComposite';
import { FormTextField } from './FormTextField';

export type ConfirmDialogProps = PropsWithChildren<{
  open?: boolean;
  action?: string;
  confirmDisabled?: boolean;
  expected?: string;
  irreversible?: boolean;
  title?: ReactNode;
  warning?: string;
  onConfirm?: () => Promise<unknown>;
  onClose?: () => void;
}>;

export const ConfirmDialog = ({
  open = true,
  action = 'Confirm',
  expected,
  children = null,
  confirmDisabled = false,
  irreversible = false,
  title,
  warning,
  onConfirm,
  onClose,
}: ConfirmDialogProps) => {
  const [value, setValue] = useState('');

  const disabled = Boolean(expected && value !== expected);

  const handleClose = () => {
    onClose?.();
  };

  const [{ loading, error }, confirm] = useAsyncFn(async () => {
    await onConfirm?.();
    handleClose();
  }, [onConfirm, onClose]);

  return (
    <DialogComposite.Root
      open={open}
      onClose={handleClose}
      variant="confirmation"
    >
      <DialogComposite.Content
        isLoading={loading}
        data-testid="confirm-dialog"
        data-track-scope="dialog.confirm"
      >
        <DialogComposite.Header>
          <DialogComposite.Title>{title}</DialogComposite.Title>
        </DialogComposite.Header>
        <DialogComposite.Body>
          <Flex direction="column" gap="base">
            {irreversible && (
              <Banner colorSet="negative" contextual role="alert">
                This action cannot be undone.
              </Banner>
            )}
            {children}
            {expected && (
              <FormTextField
                label="Confirmation"
                name="action"
                autoFocus
                value={value}
                onChange={e => setValue(e.currentTarget.value)}
                onKeyDown={event =>
                  event.key === 'Enter' && !disabled && void confirm()
                }
                placeholder={`Enter "${expected}" to confirm this action...`}
               data-track-source-anchor="c79e76fc80;confirm-dialog-form-text-field"/>
            )}
            {error && (
              <Banner colorSet="negative" contextual role="alert">
                {error.message}
              </Banner>
            )}
            {warning && (
              <Banner colorSet="warning" contextual role="alert">
                {warning}
              </Banner>
            )}
          </Flex>
        </DialogComposite.Body>
        <DialogComposite.Actions>
          <DialogComposite.Actions.Cancel onClick={handleClose}  data-track-source-anchor="5c3c93b022;confirm-dialog-cancel"/>
          <DialogComposite.Actions.Primary
            onClick={() => void confirm()}
            disabled={disabled || confirmDisabled}
           data-track-source-anchor="d86f90360b;confirm-dialog-primary">
            {action}
          </DialogComposite.Actions.Primary>
        </DialogComposite.Actions>
      </DialogComposite.Content>
    </DialogComposite.Root>
  );
};
